import { getSupabaseClient } from '@/integrations/supabase/client';
import type { ProductPromotion } from '@/types';
import {
  mapProductPromotionRow,
  type CreatePromotionDefinitionInput,
} from './promotionValidation';

const promotionSelect = `
  *,
  product_promotion_groups (
    *,
    product_promotion_options (*)
  )
`;

type SupabaseClient = ReturnType<typeof getSupabaseClient>;

async function insertPromotionGroups(
  supabase: SupabaseClient,
  promotionId: string,
  groups: CreatePromotionDefinitionInput['groups']
) {
  for (const g of groups) {
    const { data: groupRow, error: groupError } = await supabase
      .from('product_promotion_groups')
      .insert([
        {
          product_promotion_id: promotionId,
          mode: g.mode,
          required_units: g.requiredUnits,
          label: g.label ?? null,
          updated_at: new Date().toISOString(),
        },
      ])
      .select('id')
      .single();

    if (groupError) throw groupError;
    const groupId = groupRow.id as string;

    if (g.options.length > 0) {
      const optionRows = g.options.map((o) => ({
        group_id: groupId,
        component_product_id: o.componentProductId,
        default_quantity: o.defaultQuantity ?? 0,
        updated_at: new Date().toISOString(),
      }));
      const { error: optError } = await supabase.from('product_promotion_options').insert(optionRows);
      if (optError) throw optError;
    }
  }
}

export const promotionsService = {
  async getPromotionByPromoProductId(
    organizationId: string,
    promoProductId: string
  ): Promise<ProductPromotion | null> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('product_promotions')
      .select(promotionSelect)
      .eq('organization_id', organizationId)
      .eq('promo_product_id', promoProductId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    return mapProductPromotionRow(data);
  },

  async syncBundleProductEnabled(
    organizationId: string,
    promoProductId: string,
    isEnabled: boolean
  ): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('products')
      .update({
        is_enabled: isEnabled,
        updated_at: new Date().toISOString(),
      })
      .eq('id', promoProductId)
      .eq('organization_id', organizationId);

    if (error) throw error;
  },

  async setPromotionActive(
    organizationId: string,
    promotionId: string,
    isActive: boolean
  ): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('product_promotions')
      .update({
        is_active: isActive,
        updated_at: new Date().toISOString(),
      })
      .eq('id', promotionId)
      .eq('organization_id', organizationId);

    if (error) throw error;
  },

  async syncPromotionActiveFromProduct(
    organizationId: string,
    promoProductId: string,
    isEnabled: boolean
  ): Promise<void> {
    const promotion = await this.getPromotionByPromoProductId(organizationId, promoProductId);
    if (!promotion) return;

    const shouldBeActive = isEnabled !== false;
    if (promotion.isActive === shouldBeActive) return;

    await this.setPromotionActive(organizationId, promotion.id, shouldBeActive);
  },

  async getActivePromotionByPromoProductId(
    organizationId: string,
    promoProductId: string
  ): Promise<ProductPromotion | null> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('product_promotions')
      .select(promotionSelect)
      .eq('organization_id', organizationId)
      .eq('promo_product_id', promoProductId)
      .eq('is_active', true)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    return mapProductPromotionRow(data);
  },

  async getPromotionById(organizationId: string, promotionId: string): Promise<ProductPromotion | null> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('product_promotions')
      .select(promotionSelect)
      .eq('organization_id', organizationId)
      .eq('id', promotionId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    return mapProductPromotionRow(data);
  },

  async listPromotions(organizationId: string): Promise<ProductPromotion[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('product_promotions')
      .select(promotionSelect)
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapProductPromotionRow);
  },

  async assertProductsBelongToOrg(productIds: string[], organizationId: string): Promise<void> {
    if (productIds.length === 0) return;
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('products')
      .select('id')
      .eq('organization_id', organizationId)
      .in('id', productIds);

    if (error) throw error;
    const found = new Set((data ?? []).map((r: { id: string }) => r.id));
    for (const id of productIds) {
      if (!found.has(id)) {
        throw new Error(`Product ${id} does not belong to this organization or does not exist`);
      }
    }
  },

  async createPromotionDefinition(input: CreatePromotionDefinitionInput): Promise<ProductPromotion> {
    const supabase = getSupabaseClient();
    const productIds = [
      input.promoProductId,
      ...input.groups.flatMap((g) => g.options.map((o) => o.componentProductId)),
    ];
    await this.assertProductsBelongToOrg([...new Set(productIds)], input.organizationId);

    const { data: promoRow, error: promoError } = await supabase
      .from('product_promotions')
      .insert([
        {
          organization_id: input.organizationId,
          promo_product_id: input.promoProductId,
          is_active: input.isActive ?? true,
          updated_at: new Date().toISOString(),
        },
      ])
      .select('id')
      .single();

    if (promoError) throw promoError;

    const promotionId = promoRow.id as string;
    await insertPromotionGroups(supabase, promotionId, input.groups);

    const isActive = input.isActive ?? true;
    await this.syncBundleProductEnabled(input.organizationId, input.promoProductId, isActive);

    const created = await this.getPromotionById(input.organizationId, promotionId);
    if (!created) throw new Error('Failed to load promotion after create');
    return created;
  },

  async updatePromotionDefinition(
    organizationId: string,
    promotionId: string,
    input: Omit<CreatePromotionDefinitionInput, 'organizationId' | 'promoProductId'> & {
      promoProductId?: string;
      isActive?: boolean;
    }
  ): Promise<ProductPromotion> {
    const supabase = getSupabaseClient();
    const existing = await this.getPromotionById(organizationId, promotionId);
    if (!existing) throw new Error('Promotion not found');

    const promoProductId = input.promoProductId ?? existing.promoProductId;
    const productIds = [
      promoProductId,
      ...input.groups.flatMap((g) => g.options.map((o) => o.componentProductId)),
    ];
    await this.assertProductsBelongToOrg([...new Set(productIds)], organizationId);

    const { error: updPromoError } = await supabase
      .from('product_promotions')
      .update({
        promo_product_id: promoProductId,
        is_active: input.isActive ?? existing.isActive,
        updated_at: new Date().toISOString(),
      })
      .eq('id', promotionId)
      .eq('organization_id', organizationId);

    if (updPromoError) throw updPromoError;

    const { error: delErr } = await supabase
      .from('product_promotion_groups')
      .delete()
      .eq('product_promotion_id', promotionId);

    if (delErr) throw delErr;

    await insertPromotionGroups(supabase, promotionId, input.groups);

    const isActive = input.isActive ?? existing.isActive;
    await this.syncBundleProductEnabled(organizationId, promoProductId, isActive);

    const updated = await this.getPromotionById(organizationId, promotionId);
    if (!updated) throw new Error('Failed to load promotion after update');
    return updated;
  },

  async deletePromotionDefinition(organizationId: string, promotionId: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('product_promotions')
      .delete()
      .eq('id', promotionId)
      .eq('organization_id', organizationId);

    if (error) throw error;
  },
};
